import { pgTable, text, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import z from "zod";

import { usStatesEnumSchema, type UsStatesEnumType } from "./commonSchema";
import { customTimestamp } from "./utils/types";

export const clinicsTable = pgTable("clinics", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  logoBase64: text("logo_base64"),
  // Stored as text[] rather than us_states_enum[] so GCP Datastream can replicate
  // it to BigQuery (Datastream cannot map arrays of enum types). Values are still
  // validated against usStatesEnumSchema at the API layer below. See issue #512.
  states: text("states").array().$type<UsStatesEnumType[]>().notNull(),
  workspaceId: text("workspace_id"),
  createdAt: customTimestamp("created_at")
    .notNull()
    .$defaultFn(() => new Date().toISOString()),
  updatedAt: customTimestamp("updated_at")
    .notNull()
    .$defaultFn(() => new Date().toISOString())
    .$onUpdateFn(() => new Date().toISOString()),
});

export const clinicSchema = createSelectSchema(clinicsTable, {
  states: z.array(usStatesEnumSchema),
});
export type ClinicType = z.infer<typeof clinicSchema>;

export const insertClinicSchema = createInsertSchema(clinicsTable, {
  name: z.string().trim().min(1),
  logoBase64: z.string().optional(),
  states: z.array(usStatesEnumSchema).min(1),
  workspaceId: z.string().optional(),
}).omit({
  createdAt: true,
  updatedAt: true,
});
export type InsertClinicType = z.infer<typeof insertClinicSchema>;

export const clinicsOrderFieldSchema = z.enum(["NAME", "CREATED_AT"]);
export type ClinicsOrderFieldType = z.infer<typeof clinicsOrderFieldSchema>;

export const patientClinicSchema = clinicSchema.pick({ logoBase64: true });
export type PatientClinicType = z.infer<typeof patientClinicSchema>;
